import { removeBrackets } from "helpers/utils";
import { t } from "i18next";
import type { LexicalNode, ParagraphNode } from "lexical";
import { $copyNode, $isElementNode } from "lexical";

import type { TBeneficiariesFormData } from "..";
import type { VariableNode } from "../LexicalVariableNode";
import { $createVariableNode } from "../LexicalVariableNode";
import type { RepeatableContainerNode } from "./RepeatableContainerNode";
import type { RepeatableContentNode } from "./RepeatableContentNode";
import { $createRepeatableContentNode, $isRepeatableContentNode } from "./RepeatableContentNode";
import type { RepeatableTitleNode } from "./RepeatableTitleNode";
import { $isRepeatableTitleNode } from "./RepeatableTitleNode";
import type { RepeatableArticle } from "./repeatBy";

const duplicateRepeatableContainer = (
    source: RepeatableContainerNode,
    content: RepeatableContentNode,
    mapChild: (node: LexicalNode) => LexicalNode = (node) => $copyNode(node),
): RepeatableContainerNode => {
    const copy = $copyNode(source) as RepeatableContainerNode;
    const children = source.getChildren();
    const title = $copyNode(children.find((node) => $isRepeatableTitleNode(node)) as RepeatableTitleNode);
    const sourceContent = children.find((node) => $isRepeatableContentNode(node)) as RepeatableContentNode;

    sourceContent.getChildren().forEach((paragraph) => {
        const newParagraph = $copyNode(paragraph) as ParagraphNode;
        if ($isElementNode(paragraph)) {
            paragraph.getChildren().forEach((node) => {
                newParagraph.append(mapChild(node));
            });
        }
        content.append(newParagraph);
    });

    copy.append(title, content);
    return copy;
};

const contentAlreadyExpanded = (block: RepeatableContainerNode) => {
    const content = block.getChildren().find((node) => $isRepeatableContentNode(node));
    if (!$isRepeatableContentNode(content)) return false;
    if (block.getRepeatBy() === "article") return Boolean(content.getArticleIndex());
    return Boolean(content.getBeneficiaryIndex());
};

const beneficiaryChild = (beneficiaryEntity: string | undefined) => (node: LexicalNode) => {
    /**
     * Legal entities reuse the same placeholders with a different meaning.
     * {{firstName}} becomes the representative first name.
     */
    if (beneficiaryEntity === "legal" && ["{{firstName}}", "{{lastName}}"].includes((node as VariableNode)?.__text)) {
        const variable = removeBrackets((node as VariableNode).__text);
        const legalEntityVariable = `{{${variable}LegalEntity}}`;
        return $createVariableNode(
            legalEntityVariable,
            `: ${t(`ContractSettings.TemplateVariablePlaceholder.${legalEntityVariable}`)}`,
        );
    }
    return $copyNode(node);
};

export const expandBeneficiaryRepeatableBlock = (
    block: RepeatableContainerNode,
    beneficiariesFormData: TBeneficiariesFormData | undefined,
) => {
    const beneficiaryIds = beneficiariesFormData?.beneficiaryIds;
    if (!beneficiaryIds?.length || block.getRepeatBy() === "article" || contentAlreadyExpanded(block)) return;

    const titleBeneficiaries = beneficiariesFormData?.titleBeneficiaries;
    const orderedIds = [...beneficiaryIds].reverse();
    orderedIds.forEach((beneficiaryId, beneficiaryIndex) => {
        const beneficiary = titleBeneficiaries?.beneficiary[beneficiaryId];
        const content = $createRepeatableContentNode(beneficiary?.name, String(beneficiaryIndex), "beneficiary");
        const copy = duplicateRepeatableContainer(block, content, beneficiaryChild(beneficiary?.entity));
        block.insertBefore(copy);
    });
    block.remove();
};

export const expandArticleRepeatableBlock = (
    block: RepeatableContainerNode,
    articles: RepeatableArticle[] | undefined,
) => {
    if (!articles?.length || block.getRepeatBy() !== "article" || contentAlreadyExpanded(block)) return;

    articles.forEach((article, index) => {
        const content = $createRepeatableContentNode(undefined, undefined, "article", article.name, String(index));
        const copy = duplicateRepeatableContainer(block, content);
        block.insertBefore(copy);
    });
    block.remove();
};
